// src/pages/onboarding/CompetitorsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../contexts/ProjectContext';
import api from '../../utils/apiClient';

const CompetitorsPage = () => {
  const navigate = useNavigate();
  const { currentProject } = useProjects();

  const [competitors, setCompetitors]     = useState([]);
  const [competitorInput, setCompetitorInput] = useState('');
  const [loading, setLoading]             = useState(false);
  const [errorMsg, setErrorMsg]           = useState('');

  useEffect(() => {
    if (!currentProject?.id) return;
    if (Array.isArray(currentProject.project_competitors)) {
      setCompetitors(currentProject.project_competitors.map(c => c.url));
    } else {
      setCompetitors([]);
    }
  }, [currentProject?.id]); 

  const handleAddCompetitor = () => {
    if (competitorInput.trim()) {
      setCompetitors(prev => [...prev, competitorInput.trim()]);
      setCompetitorInput('');
    }
  };

  const handleRemoveCompetitor = (index) => {
    setCompetitors(prev => prev.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && competitorInput.trim()) {
      e.preventDefault();
      handleAddCompetitor();
    }
  };

  const isValidCompetitorCount = competitors.length >= 3 && competitors.length <= 7;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!currentProject?.id) {
      setErrorMsg('No project selected.');
      return;
    }
    if (!isValidCompetitorCount) {
      setErrorMsg('Please add between 3 and 7 competitors.');
      return;
    }

    setLoading(true);
    try {
      await api.projects.setCompetitors(currentProject.id, competitors);
      navigate('/onboarding/blog');
    } catch (err) {
      console.error('Error saving competitors:', err);
      setErrorMsg(err.message || 'Failed to save competitors');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 md:p-8">
      <h1 className="text-2xl font-bold text-center text-gray-900 mb-6">
        List Your Competitors
      </h1>

      {errorMsg && (
        <div className="bg-red-100 text-red-700 p-2 rounded mb-4">
          {errorMsg}
        </div>
      )}

      <div className="mb-8 bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <p className="text-blue-700 font-medium">
          For the best results, please ensure all competitors are relevant, popular, and active in your industry.
        </p>
        <ul className="mt-2 ml-5 text-blue-600 text-sm space-y-1 list-disc">
          <li>They help us identify high-quality keywords in your industry</li>
          <li>We analyze their content to find trending topics and gaps</li>
          <li>Their strategies inform our AI to create more competitive content</li>
        </ul>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <div className="flex">
            <input
              type="text"
              value={competitorInput}
              onChange={(e) => setCompetitorInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter competitor domain (e.g., competitor.com)"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            />
            <button
              type="button"
              onClick={handleAddCompetitor}
              className="px-4 py-2 border border-transparent rounded-r-md shadow-sm text-sm font-medium text-white bg-gray-800 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Competitor list */}
        <div className="space-y-2">
          {competitors.map((competitor, index) => (
            <div
              key={index}
              className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-md"
            >
              <span className="text-gray-700">{competitor}</span>
              <button
                type="button"
                onClick={() => handleRemoveCompetitor(index)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          ))}
        </div>

        <div className="pt-6">
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-[#1ba6ff] hover:bg-[#1697eb] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1ba6ff] disabled:opacity-50"
          >
            {loading ? 'Saving…' : 'Save Competitors'}
          </button>
          <p className="mt-2 text-center text-sm text-gray-500">
            Add between 3 and 7 competitors ({competitors.length})
          </p>
        </div>
      </form>
    </div>
  );
};

export default CompetitorsPage;
